import type { FeeScheduleLineItem } from '../hooks/useFeeSchedules';

export interface FeePreviewLineItem {
  heading: string;
  amountCents: number;
  recipient: string;
  chargeEffect: string;
  payoutEffect: string;
}

export interface FeePreviewResult {
  lineItems: FeePreviewLineItem[];
  payerCents: number;
  gymReceives: number;
  platformFee: number;
}

export function calculatePreview(items: FeeScheduleLineItem[], baseCents: number): FeePreviewResult {
  // Items arrive pre-sorted by display_order from database query (useFeeSchedulesWithItems)
  // Partition into 3 passes (same logic as fee-config.ts)
  const pass1 = items.filter(i => i.charge_effect === 'none');
  const pass2 = items.filter(i => i.charge_effect === 'add' && i.calculation_basis !== 'stripe_fee');
  const pass3 = items.filter(i => i.charge_effect === 'add' && i.calculation_basis === 'stripe_fee');

  const results: FeePreviewLineItem[] = [];
  let payerCents = baseCents;
  let priorDebitTotal = 0;

  function processItem(item: FeeScheduleLineItem) {
    let feeAmount = 0;

    switch (item.calculation_basis) {
      case 'from_base_amount': {
        if (item.fee_percentage) feeAmount += Math.round(baseCents * item.fee_percentage);
        if (item.flat_amount_cents) feeAmount += item.flat_amount_cents;
        break;
      }
      case 'stripe_fee': {
        const rate = item.fee_percentage || 0;
        const flat = item.flat_amount_cents || 0;
        if (item.charge_effect === 'add') {
          if (rate > 0 || flat > 0) {
            const totalWithStripe = (payerCents + flat) / (1 - rate);
            feeAmount = Math.round(totalWithStripe - payerCents);
          }
        } else {
          feeAmount = Math.round(payerCents * rate) + flat;
        }
        break;
      }
      case 'net_after_prior_fees': {
        let baseCalc = 0;
        if (item.fee_percentage) baseCalc += Math.round(baseCents * item.fee_percentage);
        if (item.flat_amount_cents) baseCalc += item.flat_amount_cents;
        feeAmount = baseCalc - priorDebitTotal;
        break;
      }
    }

    if (item.min_amount_cents) {
      feeAmount = Math.max(feeAmount, item.min_amount_cents);
    }
    feeAmount = Math.max(feeAmount, 0);

    if (item.payout_effect === 'debit') {
      priorDebitTotal += feeAmount;
    }
    if (item.charge_effect === 'add') {
      payerCents += feeAmount;
    }

    results.push({
      heading: item.line_item_heading,
      amountCents: feeAmount,
      recipient: item.fee_recipient,
      chargeEffect: item.charge_effect,
      payoutEffect: item.payout_effect,
    });
  }

  for (const item of pass1) processItem(item);
  for (const item of pass2) processItem(item);
  for (const item of pass3) processItem(item);

  // Calculate summary amounts
  const gymReceives = results
    .filter(r => r.recipient === 'gym')
    .reduce((sum, r) => r.payoutEffect === 'credit' ? sum + r.amountCents : sum - r.amountCents, 0);
  const platformFee = results
    .filter(r => r.recipient === 'rollops')
    .reduce((sum, r) => r.payoutEffect === 'credit' ? sum + r.amountCents : sum - r.amountCents, 0);

  return { lineItems: results, payerCents, gymReceives, platformFee };
}

export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
